// WS5 PR5 — Pin Sprite billboard rendering + visibility rules.
//
// Pin = THREE.Sprite (sealed) con texture pin.png Google Maps style.
// Halo bianco soffuso generato runtime via Canvas2D (radial gradient) per
// leggibilita' su scena scura/chiara — NO asset esterno aggiuntivo.
//
// Visibility rules (pure, table-driven):
//   - current viewbox → hidden worldspace.
//   - distance < PIN_NEAR_DIM_M → dim opacity + non-clickable (anti-misclick).
//   - distance > PIN_FADE_FAR_M → opacity = PIN_OPACITY_MIN.
//   - PIN_FADE_NEAR_M < distance < PIN_FADE_FAR_M → lerp.
//   - occluded by splat geometry (raycast 10Hz) → hidden.
//   - mobile + rank > PIN_MOBILE_TOP_K → hidden (anti-clutter).
//
// Click handling NON e' qui (e' nel canvas click handler dell'orchestrator
// che chiama Raycaster.intersectObjects(pinsLayer.children) e legge
// sprite.userData.clickable). Vedi orchestrator.ts wiring.

import * as THREE from 'three';
import type { ModelViewbox, Vec3 } from '../api/types.js';
import { fadeOpacity } from '../hotspots/fade.js';
import {
  PIN_FADE_FAR_M,
  PIN_FADE_NEAR_M,
  PIN_MOBILE_TOP_K,
  PIN_NEAR_DIM_M,
  PIN_OCCLUSION_CHECK_INTERVAL_MS,
  PIN_OFFSET_Y_M,
  PIN_OPACITY_DIM,
  PIN_OPACITY_MAX,
  PIN_OPACITY_MIN,
} from './types.js';

/** Metadata sprite userData. Letto da click handler. */
export interface PinSpriteUserData {
  kind: 'viewbox-pin';
  vbArrayIdx: number;
  clickable: boolean;
  occluded: boolean;
  lastOcclusionCheckMs: number;
}

/** Riferimento ai 2 sprite (main + halo) per una viewbox. */
export interface PinSpritePair {
  main: THREE.Sprite;
  halo: THREE.Sprite;
}

/** Cache del pin texture caricato una sola volta (TextureLoader async).
 *  Owned dal modulo per evitare leak su model unload. Dispose esplicito
 *  via disposeSharedTextures(). */
let sharedPinTexture: THREE.Texture | null = null;
let sharedHaloTexture: THREE.Texture | null = null;

/** Carica pin.png da public/assets/icons/. Cached. Concurrent-safe via
 *  promise reuse. */
let pinTexturePromise: Promise<THREE.Texture> | null = null;
export function loadPinTexture(baseUrl = '/'): Promise<THREE.Texture> {
  if (sharedPinTexture !== null) return Promise.resolve(sharedPinTexture);
  if (pinTexturePromise !== null) return pinTexturePromise;
  const url = `${baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`}assets/icons/pin.png`;
  pinTexturePromise = new Promise<THREE.Texture>((resolve, reject) => {
    new THREE.TextureLoader().load(
      url,
      (tex) => {
        tex.minFilter = THREE.LinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.generateMipmaps = false;
        sharedPinTexture = tex;
        resolve(tex);
      },
      undefined,
      (err) => {
        pinTexturePromise = null;
        reject(err);
      },
    );
  });
  return pinTexturePromise;
}

/** Genera halo radial gradient via Canvas2D, una sola volta. 128x128
 *  bianco-soffuso (centro alpha=0.7, edge alpha=0). Pattern: usata sotto
 *  il pin scuro per leggibilita' su scena scura/chiara. */
export function buildHaloTexture(): THREE.Texture {
  if (sharedHaloTexture !== null) return sharedHaloTexture;
  const size = 128;
  const canvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  // Fallback test/node: ritorna texture vuota (1x1). Test ambiente happy-dom
  // ha document.createElement, quindi ramo principale.
  if (canvas === null) {
    const tex = new THREE.Texture();
    sharedHaloTexture = tex;
    return tex;
  }
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    const tex = new THREE.Texture();
    sharedHaloTexture = tex;
    return tex;
  }
  const center = size / 2;
  const gradient = ctx.createRadialGradient(center, center, 0, center, center, center);
  gradient.addColorStop(0.0, 'rgba(255, 255, 255, 0.7)');
  gradient.addColorStop(0.4, 'rgba(255, 255, 255, 0.35)');
  gradient.addColorStop(1.0, 'rgba(255, 255, 255, 0.0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.generateMipmaps = false;
  sharedHaloTexture = tex;
  return tex;
}

/** Dispose textures shared (call su viewer dispose). Pure cleanup. */
export function disposeSharedTextures(): void {
  if (sharedPinTexture !== null) {
    sharedPinTexture.dispose();
    sharedPinTexture = null;
  }
  if (sharedHaloTexture !== null) {
    sharedHaloTexture.dispose();
    sharedHaloTexture = null;
  }
  pinTexturePromise = null;
}

/** Calcola Y pin worldspace per una viewbox.
 *  Priority: floor_y se presente, altrimenti default_camera.pos[1] - eye_height.
 *  + eye_height_m (o 1.65) + PIN_OFFSET_Y_M (~30cm sopra occhi).
 *  Pure. */
export function computePinPositionY(vb: ModelViewbox, defaultEyeHeight = 1.65): number {
  const eyeH = Number.isFinite(vb.eye_height_m) ? (vb.eye_height_m as number) : defaultEyeHeight;
  let floorY: number;
  if (Number.isFinite(vb.floor_y)) {
    floorY = vb.floor_y as number;
  } else {
    // Fallback: default_camera.pos.y - eyeH (= floor secondo legacy).
    const headY = vb.default_camera.pos[1];
    floorY = Number.isFinite(headY) ? headY - eyeH : 0;
  }
  return floorY + eyeH + PIN_OFFSET_Y_M;
}

/** Calcola XZ pin worldspace per una viewbox.
 *  Uso `default_camera.pos[0/2]` (spawn point della viewbox) invece di
 *  `headbox.center[0/2]`. Razionale: in scene con headbox-PER-stanza i due
 *  coincidono. In scene con headbox FULL-SCENE condiviso (es. Roma 4vb
 *  hotfix fase 1) il `headbox.center` e' UGUALE per tutte le viewbox →
 *  i pin si sovrapporrebbero nello stesso punto. `default_camera.pos`
 *  e' lo spawn point unico per ogni viewbox (calibrato Seurat). Pure. */
export function computePinPositionXZ(vb: ModelViewbox): { x: number; z: number } {
  return { x: vb.default_camera.pos[0], z: vb.default_camera.pos[2] };
}

/** Costruisce pin sprite per una viewbox (main + halo). pin texture deve
 *  essere caricata prima (loadPinTexture). halo texture e' generata se
 *  non gia' cached. */
export function createPinSprite(
  viewbox: ModelViewbox,
  arrayIdx: number,
  pinTexture: THREE.Texture,
  defaultEyeHeight = 1.65,
): PinSpritePair {
  // PR5 fix 2026-06-05: usa default_camera.pos[X/Z] (spawn point unico per
  // viewbox) invece di headbox.center. Roma 4vb headbox FULL-SCENE = stesso
  // center per tutti i vb → pin sovrapposti. Vedi computePinPositionXZ docstring.
  const { x: cx, z: cz } = computePinPositionXZ(viewbox);
  const cy = computePinPositionY(viewbox, defaultEyeHeight);

  // Halo sprite (sotto il pin, scale leggermente piu' grande).
  const haloMaterial = new THREE.SpriteMaterial({
    map: buildHaloTexture(),
    transparent: true,
    opacity: 0.6,
    depthWrite: false,
    depthTest: true,
    sizeAttenuation: true,
    toneMapped: false,
    blending: THREE.NormalBlending,
  });
  const haloSprite = new THREE.Sprite(haloMaterial);
  haloSprite.position.set(cx, cy, cz);
  // Halo scale: 0.9m x 0.95m (square-ish, leggermente piu' alto).
  haloSprite.scale.set(0.9, 0.95, 1);
  haloSprite.renderOrder = 1; // render dopo splat (depthWrite=false su splat)
  haloSprite.userData = { kind: 'halo', vbArrayIdx: arrayIdx };

  // Pin main sprite.
  const pinMaterial = new THREE.SpriteMaterial({
    map: pinTexture,
    transparent: true,
    opacity: PIN_OPACITY_MAX,
    depthWrite: false,
    depthTest: true,
    sizeAttenuation: true,
    toneMapped: false,
  });
  const mainSprite = new THREE.Sprite(pinMaterial);
  mainSprite.position.set(cx, cy, cz);
  // Pin scale: 0.5m wide × 0.55m tall (pin aspect ~512/537 ≈ 0.95).
  mainSprite.scale.set(0.5, 0.55, 1);
  mainSprite.renderOrder = 2; // render sopra halo
  const userData: PinSpriteUserData = {
    kind: 'viewbox-pin',
    vbArrayIdx: arrayIdx,
    clickable: true,
    occluded: false,
    lastOcclusionCheckMs: 0,
  };
  mainSprite.userData = userData;

  return { main: mainSprite, halo: haloSprite };
}

/** Computa ranking screen-distance da centro viewport per mobile pin cap.
 *  Pure. Ritorna array di [arrayIdx, screenDistFromCenter] ordinato asc.
 *  Caller filtra top-K (PIN_MOBILE_TOP_K). */
export function rankPinsByScreenDistance(
  pinPositions: readonly { vbArrayIdx: number; world: Vec3 }[],
  camera: THREE.PerspectiveCamera,
  viewport: { width: number; height: number },
): { vbArrayIdx: number; screenDist: number; behind: boolean }[] {
  const cx = viewport.width / 2;
  const cy = viewport.height / 2;
  const v = new THREE.Vector3();
  return pinPositions
    .map((p) => {
      v.set(p.world[0], p.world[1], p.world[2]);
      v.project(camera);
      const behind = v.z > 1 || v.z < -1;
      const sx = ((v.x + 1) / 2) * viewport.width;
      const sy = ((1 - v.y) / 2) * viewport.height;
      const screenDist = Math.sqrt((sx - cx) * (sx - cx) + (sy - cy) * (sy - cy));
      return { vbArrayIdx: p.vbArrayIdx, screenDist, behind };
    })
    .sort((a, b) => a.screenDist - b.screenDist);
}

/** Result di pin visibility update per UN pin. Pure. */
export interface PinVisibilityDecision {
  visible: boolean;
  opacity: number;
  clickable: boolean;
  shouldCheckOcclusion: boolean;
}

/** Computa visibility decision per UN pin. Pure (no side-effect su sprite,
 *  caller applica). Pattern: usata per testabilita' + applicare regola
 *  caso per caso senza three-mock complesso. */
export function computePinVisibility(args: {
  vbArrayIdx: number;
  currentArrayIdx: number;
  distanceCamPin: number;
  occluded: boolean;
  mobileVisibleSet: Set<number> | null; // null = desktop (no cap)
  lastOcclusionCheckMs: number;
  nowMs: number;
}): PinVisibilityDecision {
  // Rule 1: current viewbox → hidden worldspace.
  if (args.vbArrayIdx === args.currentArrayIdx) {
    return { visible: false, opacity: 0, clickable: false, shouldCheckOcclusion: false };
  }
  // Rule 6: mobile cap (top-K).
  if (args.mobileVisibleSet !== null && !args.mobileVisibleSet.has(args.vbArrayIdx)) {
    return { visible: false, opacity: 0, clickable: false, shouldCheckOcclusion: false };
  }
  // Rule 5: occlusion (check throttle decision in shouldCheckOcclusion).
  const shouldCheckOcclusion =
    args.nowMs - args.lastOcclusionCheckMs >= PIN_OCCLUSION_CHECK_INTERVAL_MS;
  if (args.occluded) {
    return { visible: false, opacity: 0, clickable: false, shouldCheckOcclusion };
  }
  // Rule 2: distance < dim threshold → dim + non-clickable.
  if (args.distanceCamPin < PIN_NEAR_DIM_M) {
    return {
      visible: true,
      opacity: PIN_OPACITY_DIM,
      clickable: false,
      shouldCheckOcclusion,
    };
  }
  // Rule 3+4: fade by distance.
  const fade = fadeOpacity(
    args.distanceCamPin,
    PIN_FADE_NEAR_M,
    PIN_FADE_FAR_M,
    PIN_OPACITY_MAX,
    PIN_OPACITY_MIN,
  );
  return { visible: true, opacity: fade, clickable: true, shouldCheckOcclusion };
}

/** Mobile pin cap: ritorna set di vbArrayIdx che dovrebbero essere visibili
 *  (top-K screen-distance). Caller passa null per desktop. */
export function buildMobileVisibleSet(
  pinPositions: readonly { vbArrayIdx: number; world: Vec3 }[],
  camera: THREE.PerspectiveCamera,
  viewport: { width: number; height: number },
  topK: number = PIN_MOBILE_TOP_K,
): Set<number> {
  const ranked = rankPinsByScreenDistance(pinPositions, camera, viewport);
  const out = new Set<number>();
  let added = 0;
  for (const r of ranked) {
    if (added >= topK) break;
    if (r.behind) continue;
    out.add(r.vbArrayIdx);
    added += 1;
  }
  return out;
}
